import type { CatalogListConfig } from './types';
import { worldBookGateCard, type WorldBookGate } from './world-book-gate';
import { bookChapters } from './world-book-chapters';
import { initTooltips, resolveUrl, results, setStatus, showLoader, hideLoader } from './catalog-list';

interface Place {
    id: string; name: string; type_name: string; type_icon: string; url: string;
    thumb_url: string; count: number; children: Place[];
}
interface Article {
    id: string; name: string; excerpt: string; type: string; type_icon: string;
    is_category: number; url: string; count: number; children?: Article[];
}
interface Book {
    ok: boolean; error?: string;
    world: { id: string; name: string; type_icon: string; picture_url: string; foreword_html: string;
        can_edit: boolean; edit_url: string; add_location_url: string; add_article_url: string };
    stats: { continents: number; places: number; articles: number; events: number };
    gate: WorldBookGate; atlas: Place[]; chapters: Article[];
    media: { pictures: { url: string; alt: string }[]; map_url: string; map_view_url: string };
    details: { section: string; cards: { emoji: string; title: string; html: string; locked: boolean }[] }[];
    history: { tracks: { id: string; name: string; event_count: number; era_from: string | null; era_to: string | null; url: string }[];
        eras: { id: string; name: string; label: string; start_sort_year: number | null; end_sort_year: number | null }[];
        last_sort_year: number | null };
    index: { tag_count: number; url: string };
}
function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls = '', text = ''): HTMLElementTagNameMap[K] {
    const node = document.createElement(tag);
    node.className = cls;
    node.textContent = text;
    return node;
}
function glyph(key: string, size = 22): HTMLElement {
    const node = el('span', 'ft-wb-icon');
    node.innerHTML = typeof iconMap === 'function' ? iconMap(key || 'location', size, size, false) : '';
    node.setAttribute('aria-hidden', 'true');
    return node;
}
function link(label: string, url: string, cls = ''): HTMLAnchorElement {
    const a = el('a', cls, label);
    a.href = url.startsWith('#') ? url : resolveUrl(url);
    return a;
}
function articleCard(article: Article): HTMLElement {
    const card = link('', article.url, 'ft-wb-article-card');
    card.append(glyph(article.type_icon || (article.is_category ? 'category' : 'article'), 30));
    const copy = el('div', 'ft-wb-card-copy');
    copy.append(el('span', 'ft-wb-card-type', article.type || (article.is_category ? 'Collection' : 'Article')),
        el('h3', 'ft-wb-card-name', article.name));
    if (article.excerpt) copy.append(el('p', 'ft-wb-excerpt', article.excerpt));
    if (article.count) copy.append(el('span', 'ft-wb-chip', `${article.count} articles`));
    card.append(copy);
    return card;
}

export function mountCatalogWorldBook(root: HTMLElement, config: CatalogListConfig): void {
    const container = results(root);
    if (!container) return;
    const id = root.getAttribute('data-ft-location-id') || '';
    const load = async (): Promise<void> => {
        showLoader(root, config);
        try {
            const url = new URL(resolveUrl(config.endpoint), window.location.href);
            url.searchParams.set('location_id', id);
            const res = await fetch(url, { credentials: 'same-origin' });
            const book = await res.json() as Book;
            if (!res.ok || book.ok !== true) throw new Error(book.error || `HTTP ${res.status}`);
            hideLoader(root);
            setStatus(root, '');
            container.textContent = '';
            container.classList.add('ft-world-book');
            const { world, gate } = book;
            const historyData = Array.isArray(book.history) ? { tracks: [], eras: [], last_sort_year: null } : book.history;
            const sampled = gate.gated && !gate.entitled;
            const cover = el('header', `ft-wb-cover${world.picture_url ? ' has-picture' : ''}`);
            cover.setAttribute('data-wb-cover', '');
            if (world.picture_url) {
                const img = el('img', 'ft-wb-cover-image'); img.src = world.picture_url; img.alt = ''; cover.append(img);
            }
            if (world.can_edit) {
                const tools = el('div', 'ft-wb-tools');
                tools.append(link('Edit world', world.edit_url, 'btn btn-sm btn-dark'),
                    link('Add Location', world.add_location_url, 'btn btn-sm btn-dark'),
                    link('Add Article', world.add_article_url, 'btn btn-sm btn-dark'));
                const notesButton = el('button', 'btn btn-sm btn-dark', 'GM notes'); notesButton.type = 'button';
                notesButton.setAttribute('aria-controls', `wb-gm-${id}`); notesButton.setAttribute('aria-expanded', 'false');
                tools.append(notesButton); cover.append(tools);
                const notes = root.parentElement?.querySelector<HTMLElement>('[data-wb-gm-slot]');
                if (notes) {
                    notes.id = `wb-gm-${id}`; notes.classList.add('ft-wb-gm-drawer');
                    notesButton.addEventListener('click', () => {
                        notes.hidden = !notes.hidden;
                        notesButton.setAttribute('aria-expanded', String(!notes.hidden));
                    });
                    // Move the actual mount, preserving its existing renderer and controls.
                    container.append(notes);
                }
            }
            const title = el('div', 'ft-wb-cover-title');
            title.append(el('div', 'ft-wb-kicker', 'A FateTide World Book'));
            const heading = el('h1'); heading.append(glyph(world.type_icon, 40), document.createTextNode(world.name)); title.append(heading);
            const stats = el('div', 'ft-wb-stats');
            for (const [count, label] of [[book.stats.continents, 'continents'], [book.stats.places, 'places'], [book.stats.articles, 'articles'], [book.stats.events, 'events']] as const) {
                stats.append(el('span', 'ft-wb-chip', `${count} ${label}`));
            }
            title.append(stats); cover.append(title); container.prepend(cover);
            const chapters = bookChapters(book);
            const contents = (): HTMLElement => {
                const nav = el('nav', 'ft-wb-toc'); nav.setAttribute('aria-label', 'World Book contents');
                nav.append(el('div', 'ft-wb-kicker', 'Contents'));
                chapters.forEach(ch => {
                    const a = link(`${ch.numeral}. ${ch.title}`, `#wb-ch-${ch.key}`);
                    a.setAttribute('data-wb-toc', ch.key); nav.append(a);
                    if (ch.key === 'atlas') book.atlas.forEach(p => nav.append(link(p.name, p.url, 'ft-wb-toc-place')));
                });
                return nav;
            };
            const mobile = el('div', 'ft-wb-mobile-contents');
            const open = el('button', 'btn ft-wb-contents-pill', '☰ Contents'); open.type = 'button';
            open.setAttribute('data-bs-toggle', 'offcanvas'); open.setAttribute('data-bs-target', `#wb-contents-${id}`);
            open.setAttribute('aria-controls', `wb-contents-${id}`); mobile.append(open); container.append(mobile);
            const offcanvas = el('div', 'offcanvas offcanvas-start ft-wb-offcanvas'); offcanvas.id = `wb-contents-${id}`; offcanvas.tabIndex = -1;
            offcanvas.setAttribute('aria-label', `${world.name} contents`);
            const close = el('button', 'btn-close btn-close-white ms-auto m-3'); close.type = 'button';
            close.setAttribute('data-bs-dismiss', 'offcanvas'); close.setAttribute('aria-label', 'Close contents');
            const mobileNav = contents();
            mobileNav.querySelectorAll('[data-wb-toc]').forEach(a => a.addEventListener('click', () => close.click()));
            offcanvas.append(close, mobileNav); container.append(offcanvas);
            const layout = el('div', 'ft-wb-layout');
            const rail = el('aside', 'ft-wb-rail'); rail.setAttribute('data-wb-contents', ''); rail.append(contents());
            const body = el('div', 'ft-wb-body'); layout.append(rail, body); container.append(layout);
            const foreword = el('section', 'ft-lore-panel ft-lore-panel--read-aloud ft-wb-foreword');
            foreword.append(el('h2', 'ft-wb-kicker', 'Foreword'));
            const prose = el('div', 'ft-wb-prose');
            // Escaped server mount from ft_parse_text_mount, hydrated after insertion below.
            prose.innerHTML = world.foreword_html; foreword.append(prose); body.append(foreword);
            if (book.media.pictures.length > 1 || book.media.map_url) {
                const media = el('section', 'ft-wb-media'); media.setAttribute('data-wb-media', '');
                media.append(el('h2', 'ft-wb-kicker', 'Gallery & Map'));
                const strip = el('div', 'ft-wb-media-strip');
                for (const picture of book.media.pictures) {
                    const card = link('', picture.url, 'ft-wb-media-card');
                    const img = el('img'); img.src = picture.url; img.alt = picture.alt; img.loading = 'lazy';
                    card.append(img); strip.append(card);
                }
                if (book.media.map_url) {
                    const card = link('', book.media.map_view_url, 'ft-wb-media-card ft-wb-map');
                    const img = el('img'); img.src = book.media.map_url; img.alt = `${world.name} map`; img.loading = 'lazy';
                    card.append(img, el('span', '', 'Open map')); strip.append(card);
                }
                media.append(strip); body.append(media);
            }
            if (sampled) body.append(worldBookGateCard(gate, world.name));
            const chapter = (key: string): HTMLElement => {
                const spec = chapters.find(c => c.key === key)!;
                const section = el('section', 'ft-wb-chapter'); section.id = `wb-ch-${key}`;
                section.setAttribute('data-wb-chapter', key);
                section.append(el('div', 'ft-wb-kicker', `Chapter ${spec.numeral}`), el('h2', 'ft-wb-chapter-title', spec.title));
                body.append(section); return section;
            };
            const atlas = chapter('atlas');
            if (book.atlas.length) atlas.append(el('p', 'ft-wb-intro', 'Choose a place to explore its lands, settlements, and stories.'));
            const grid = el('div', 'ft-wb-atlas-grid');
            const tree = el('div', 'ft-wb-place-tree'); tree.setAttribute('data-wb-tree', ''); tree.id = `wb-atlas-tree-${id}`; tree.hidden = true;
            let activeButton: HTMLButtonElement | null = null;
            const cards: HTMLElement[] = [];
            const positionTree = (): void => {
                const card = activeButton?.parentElement;
                if (!card) return;
                tree.hidden = true;
                const sameRow = cards.filter(c => c.offsetTop === card.offsetTop);
                (sameRow[sameRow.length - 1] || card).after(tree);
                tree.hidden = false;
            };
            const buildTree = (places: Place[]): HTMLElement => {
                const list = el('ul');
                places.forEach(p => {
                    const li = el('li'), row = el('div', 'ft-wb-tree-row');
                    row.append(glyph(p.type_icon, 18), link(p.name, p.url), el('span', 'ft-wb-card-type', p.type_name)); li.append(row);
                    if (p.children.length) li.append(buildTree(p.children)); list.append(li);
                });
                return list;
            };
            book.atlas.forEach(place => {
                const card = el('div', 'ft-wb-place-card'); card.setAttribute('data-wb-place-id', place.id);
                const toggle = el('button', 'ft-wb-place-toggle'); toggle.type = 'button'; toggle.setAttribute('data-wb-toggle', '');
                toggle.setAttribute('aria-expanded', 'false'); toggle.setAttribute('aria-controls', tree.id);
                const art = el('span', 'ft-wb-place-art');
                if (place.thumb_url) {
                    const img = el('img'); img.src = place.thumb_url; img.alt = ''; img.loading = 'lazy'; art.append(img);
                } else art.append(glyph(place.type_icon, 54));
                const copy = el('span', 'ft-wb-place-copy');
                const count = el('span', 'ft-wb-place-count', `${place.count} places`);
                const chevron = el('span', 'ft-wb-chevron'); chevron.setAttribute('aria-hidden', 'true');
                chevron.innerHTML = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><path d="m6 9 6 6 6-6"/></svg>';
                count.append(chevron);
                copy.append(el('span', 'ft-wb-card-type', place.type_name), el('span', 'ft-wb-card-name', place.name), count); toggle.append(art, copy);
                toggle.addEventListener('click', () => {
                    const wasOpen = activeButton === toggle;
                    activeButton?.setAttribute('aria-expanded', 'false'); tree.hidden = true; activeButton = wasOpen ? null : toggle;
                    if (wasOpen) return;
                    toggle.setAttribute('aria-expanded', 'true'); tree.textContent = '';
                    tree.append(link(`Explore ${place.name} →`, place.url, 'ft-wb-tree-heading'));
                    tree.append(place.children.length ? buildTree(place.children) : el('p', 'text-muted m-0', 'No further places recorded.'));
                    positionTree(); initTooltips(tree);
                });
                card.append(toggle); cards.push(card); grid.append(card);
            });
            grid.append(tree); atlas.append(grid);
            let gridWidth = grid.getBoundingClientRect().width;
            const gridObserver = new ResizeObserver(entries => {
                const width = entries[0]?.contentRect.width ?? gridWidth;
                if (Math.abs(width - gridWidth) > 1) { gridWidth = width; positionTree(); }
            });
            gridObserver.observe(grid);
            window.addEventListener('pagehide', () => gridObserver.disconnect(), { once: true });
            if (!book.atlas.length) atlas.append(el('p', 'text-muted', 'No places recorded yet.'));
            const almanac = chapter('almanac');
            for (const detail of book.details) {
                almanac.append(el('h3', 'ft-detail-section-title h5 mt-4 mb-3', detail.section));
                const cards = el('div', 'ft-detail-grid mb-3');
                for (const item of detail.cards) {
                    const card = el('div', 'ft-detail-card');
                    const title = el('h4', 'ft-detail-card-title h6');
                    title.append(el('span', 'ft-detail-emoji', item.emoji));
                    if (item.locked) {
                        card.setAttribute('data-wb-locked', '');
                        title.append(glyph('private', 14), el('span', 'visually-hidden', 'Locked: '));
                    }
                    title.append(document.createTextNode(item.title)); card.append(title);
                    if (!item.locked) {
                        const prose = el('div'); prose.innerHTML = item.html; card.append(prose);
                    }
                    cards.append(card);
                }
                almanac.append(cards);
            }
            if (!book.details.length) almanac.append(el('p', 'text-muted', 'No almanac entries recorded yet.'));
            book.chapters.forEach(rootArticle => {
                const section = chapter(rootArticle.id);
                if (rootArticle.excerpt && rootArticle.is_category) section.append(el('p', 'ft-wb-intro', rootArticle.excerpt));
                const cards = el('div', `ft-wb-article-grid${rootArticle.is_category ? '' : ' is-leaf'}`);
                const entries = rootArticle.is_category && rootArticle.children?.length ? rootArticle.children : [rootArticle];
                entries.forEach(article => cards.append(articleCard(article))); section.append(cards);
            });
            const history = chapter('history');
            if (historyData.eras.length) {
                const strip = el('div', 'ft-wb-era-strip'); strip.setAttribute('data-wb-era-strip', '');
                strip.setAttribute('aria-label', `${world.name} eras`);
                const band = el('div', 'ft-wb-era-band');
                const years = historyData.eras.flatMap(era => [era.start_sort_year, era.end_sort_year])
                    .filter((year): year is number => year !== null);
                const last = historyData.last_sort_year ?? Math.max(0, ...years);
                // Beginningless eras have no measurable span: give them the minimum weight.
                // Open ends extend to the last recorded date, never the real-world calendar year.
                for (const era of historyData.eras) {
                    const end = era.end_sort_year ?? last;
                    const segment = link('', `world_timeline.php?world_location_id=${encodeURIComponent(world.id)}`, 'ft-wb-era-segment');
                    segment.setAttribute('data-wb-era', era.id);
                    segment.style.flexGrow = String(era.start_sort_year === null ? 1 : Math.max(1, end - era.start_sort_year));
                    segment.append(el('span', 'ft-wb-era-name', era.name), el('span', 'ft-wb-era-label', era.label));
                    band.append(segment);
                }
                strip.append(band); history.append(strip);
            }
            historyData.tracks.forEach(track => {
                const row = link('', track.url, 'ft-wb-history-row');
                row.append(glyph('calendar_time'), el('span', 'ft-wb-history-name', track.name), el('span', 'ft-wb-chip', `${track.event_count} events`));
                if (track.era_from || track.era_to) row.append(el('span', 'ft-wb-card-type', [track.era_from, track.era_to].filter(Boolean).join(' – ')));
                row.append(el('span', '', '→')); history.append(row);
            });
            if (!historyData.tracks.length && !historyData.eras.length) history.append(el('p', 'text-muted', 'No history recorded yet.'));
            const gazetteer = root.parentElement?.querySelector<HTMLElement>('[data-wb-chapter="gazetteer"]');
            if (gazetteer) {
                gazetteer.classList.add('ft-wb-chapter');
                const kicker = gazetteer.querySelector('[data-wb-gazetteer-kicker]');
                if (kicker) kicker.textContent = `Chapter ${chapters.find(c => c.key === 'gazetteer')!.numeral}`;
                body.append(gazetteer);
            }
            const index = chapter('index');
            if (book.index.tag_count > 0) {
                index.append(el('p', 'ft-wb-intro', 'Follow the connections between the people, places, and ideas of this world.'),
                    link(`Browse all ${book.index.tag_count} tags →`, book.index.url, 'ft-wb-index-link'));
            } else index.append(el('p', 'text-muted', 'No tags recorded yet.'));
            if (sampled) body.append(worldBookGateCard(gate, world.name));
            initTooltips(container); window.ftMountParsedText?.(container);
            const sections = [...body.querySelectorAll<HTMLElement>('[data-wb-chapter]')];
            const mark = (key: string): void => {
                container.querySelectorAll<HTMLElement>('[data-wb-toc]').forEach(a => {
                    const active = a.getAttribute('data-wb-toc') === key;
                    a.classList.toggle('is-active', active);
                    if (active) a.setAttribute('aria-current', 'location'); else a.removeAttribute('aria-current');
                });
            };
            mark('atlas');
            const inView = new Set<HTMLElement>();
            const observer = new IntersectionObserver(entries => {
                entries.forEach(entry => { if (entry.isIntersecting) inView.add(entry.target as HTMLElement); else inView.delete(entry.target as HTMLElement); });
                const current = sections.find(section => inView.has(section));
                if (current) mark(current.getAttribute('data-wb-chapter') || 'atlas');
            }, { rootMargin: '-110px 0px -45% 0px' });
            sections.forEach(section => observer.observe(section));
            window.addEventListener('pagehide', () => observer.disconnect(), { once: true });
            if (window.location.hash.startsWith('#wb-ch-')) document.getElementById(window.location.hash.slice(1))?.scrollIntoView();
        } catch (error) {
            hideLoader(root); console.error('[world-book] fetch/render failed', error);
            setStatus(root, error instanceof Error ? error.message : 'Could not open this World Book.');
        }
    };
    void load();
}
